import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { 
  ChevronLeft, Activity, Loader, AlertCircle, CheckCircle,
  UserPlus, CreditCard, UserCog, KeyRound, LogIn, 
  Building2, Plus, FileText, UserMinus 
} from 'lucide-react';

import NewUserActivityLogService from '../../services/user-activity-log-service';

import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../components/ToastConfig';
import { datetimeFormatter } from '../../utils/DatetimeFormatter';

// 事件類型與圖示映射
const getActivityIcon = (activityTypeName) => {
  const iconMap = {
    'AccountCreate': { icon: UserPlus, color: 'bg-green-500 text-white' },
    'AccountCustomerCreate': { icon: CreditCard, color: 'bg-yellow-500 text-white' },
    'EditProfile': { icon: UserCog, color: 'bg-blue-500 text-white' },
    'ChangePassword': { icon: KeyRound, color: 'bg-blue-600 text-white' },
    'Login': { icon: LogIn, color: 'bg-teal-500 text-white' },
    'EditCompanyProfile': { icon: Building2, color: 'bg-pink-500 text-white' },
    'Subscribed': { icon: Plus, color: 'bg-orange-500 text-white' },
    'InvoicePaid': { icon: FileText, color: 'bg-amber-500 text-white' },
    'CancelSubscription': { icon: UserMinus, color: 'bg-red-500 text-white' },
    'CreateCompany': { icon: Building2, color: 'bg-green-600 text-white' },
    'AddCompany': { icon: Building2, color: 'bg-purple-500 text-white' },
    'LeaveCompany': { icon: Building2, color: 'bg-gray-600 text-white' },
    'RemovedFromCompany': { icon: UserMinus, color: 'bg-red-600 text-white' },
    'InvitedToCompany': { icon: UserPlus, color: 'bg-indigo-500 text-white' },
  };

  // 默認圖示（如果找不到對應的事件類型）
  return iconMap[activityTypeName] || { icon: Activity, color: 'bg-gray-500 text-white' };
};

// Timeline 項目組件
function TimelineItem({ item, isLast }) {
  const { icon: IconComponent, color } = getActivityIcon(item.activityType);
  
  return (
    <div className="flex items-start mb-6 animate-fade-in">
      <div className="flex flex-col items-center mr-4 flex-shrink-0 translate-y-1/4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110 ${color}`}>
          <IconComponent size={20} />
        </div>
        {/* 連接線 */}
        {!isLast && <div className="w-0.5 bg-gray-200 h-6 mt-2"></div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="rounded-lg p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-1">
            <h3 className=" text-gray-900">{item.activityTypeName}</h3>
          </div>
          {item.description && (
            <p className="text-sm text-gray-600 mb-2">{item.description}</p>
          )}
          <p className="text-xs text-gray-400">{item.createdAt ? datetimeFormatter(item.createdAt) : "(No data)"}</p>
        </div>
      </div>
    </div>
  );
};

function MemberActivityDetails() {
  const Navigate = useNavigate();
  const { id } = useParams();
  const [timelineData, setTimelineData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const observerRef = useRef();
  const loadingRef = useRef();
  const pageSize = 10;
  
  const { logout } = useAuth();

  const fetchData = useCallback(async (page) => {
    setLoading(true);
    setError(null);

    try {
      const res = await NewUserActivityLogService.search(id, "", page);
      const data = res.data;
      
      return {
        data: data.data, // 直接使用 API 返回的當前頁資料
        hasMore: page * pageSize < data.pagination.totalCount, // 檢查是否還有更多資料
        totalCount: data.pagination.totalCount
      };
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else if (error?.response?.status === 400 && error?.response?.data?.errorCode === '2000') {
        Navigate('/member/activity');
        return;
      } else {
        showToast.error('Failed to fetch data.');
      }
      throw error; // 重新拋出錯誤以便上層處理
    } finally {
      setLoading(false);
    }
  }, [id, pageSize, logout, Navigate]); // 添加所有依賴項

  // 載入更多資料
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    
    try {
      const result = await fetchData(currentPage);
      
      if (result && result.data) {
        setTimelineData(prev => [...prev, ...result.data]);
        setHasMore(result.hasMore);
        setCurrentPage(prev => prev + 1);
      }
    } catch (error) {
      console.error('Failed to load more data:', error);
      setError(error.message || 'Failed to load data');
    }
  }, [currentPage, loading, hasMore, fetchData]);

  // Intersection Observer 設置
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && !loading && hasMore) {
          loadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore, loading, hasMore]);

  // 初始載入
  useEffect(() => {
    if (!id || !/^\d+$/.test(id)) {
      Navigate("/member/activity");
      return;
    }

    const initialLoad = async () => {
      try {
        setCurrentPage(1); // 重置頁碼
        setTimelineData([]); // 清空現有資料
        
        const result = await fetchData(1);
        
        if (result && result.data) {
          if (result.data.length === 0 || !result.totalCount) {
            Navigate("/member/activity");
          } else {
            setTimelineData(result.data);
            setHasMore(result.hasMore);
            setCurrentPage(2); // 下次載入第2頁
          }
        }
      } catch (error) {
        console.error('Initial load failed:', error);
        setError(error.message || 'Initial load failed');
      }
    };
    
    initialLoad();
  }, [id, Navigate, fetchData]);

  // 重試函數
  const retry = () => {
    setError(null);
    loadMore();
  };

  return (
    <div className="container mx-auto p-6">

      <button onClick={() => Navigate(-1)} className="text-brandDefault group flex items-center w-fit p-3 -ml-3">
          <ChevronLeft size={36} className="text-brandDefault mr-1" />
          <span className="font-bold">Back</span>
        </button>

      <div className='grid grid-cols-2'>
        <div className='col-span-1'>
          {/* Timeline 內容 */}
          <div className="rounded-xl p-6">
            {timelineData.length === 0 && !loading ? (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No activities found</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className='font-bold text-lg'>Timeline</p>
                {timelineData.map((item, index) => (
                  <TimelineItem 
                    key={`${item.id}-${index}`}
                    item={item} 
                    isLast={index === timelineData.length - 1 && !hasMore}
                  />
                ))}
              </div>
            )}

            {/* 載入狀態 */}
            <div ref={loadingRef} className="py-8">
              {loading && (
                <div className="flex flex-col items-center gap-3">
                  <Loader className="animate-spin text-blue-500" size={32} />
                  <p className="text-gray-500 text-sm">Loading more activities...</p>
                </div>
              )}

              {error && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg border border-red-200">
                    <AlertCircle size={16} />
                    <span className="text-sm">Failed to load: {error}</span>
                  </div>
                  <button
                    onClick={retry}
                    className="ml-3 px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='col-span-1 py-6'>
          <div className='border border-black rounded-xl w-3/4 mx-auto py-6 px-10'>
            <p className="font-medium">{timelineData?.[0]?.user?.firstName} {timelineData?.[0]?.user?.lastName}</p>
            {timelineData?.[0]?.user?.id && (
              <Link
                to={`/member/account/${timelineData[0].user.id}`}
                className="text-blue-600 hover:underline text-sm"
              >
                {timelineData?.[0]?.user?.email}
              </Link>
            )}
            {!timelineData?.[0]?.user?.id && (
              <p className="text-sm text-gray-600">{timelineData?.[0]?.user?.email}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MemberActivityDetails;